import React, { useState, useEffect, useCallback } from "react";
import { Lock, RefreshCw, LogOut, Inbox, Boxes, MessagesSquare, Download, Search, BarChart3, Settings, Receipt } from "lucide-react";
import AdminInventory from "./AdminInventory";
import AdminInsights from "./AdminInsights";
import AdminSettings from "./AdminSettings";
import AdminSales from "./AdminSales";
import AdminPhotos from "./AdminPhotos";
import { Camera } from "lucide-react";
import useNoIndex from "../hooks/useNoIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFY_STYLE = {
  sent: { background: "rgba(45,106,79,0.12)", color: "var(--green-800)" },
  failed: { background: "rgba(255,63,31,0.12)", color: "#c0341a" },
  pending: { background: "rgba(245,180,0,0.16)", color: "#8a6a00" },
  skipped: { background: "#eef1ee", color: "#5a6b62" },
  legacy: { background: "#eef1ee", color: "#8a95b0" },
  not_attempted: { background: "#eef1ee", color: "#8a95b0" },
};

function NotifyBadge({ label, status, error, at }) {
  const s = status || "pending";
  const style = NOTIFY_STYLE[s] || NOTIFY_STYLE.skipped;
  const title = [
    `${label}: ${s}`,
    error ? `Reason: ${error}` : "",
    at ? `At: ${new Date(at).toLocaleString("en-IN")}` : "",
  ].filter(Boolean).join("\n");
  return (
    <span data-testid={`notify-${label.toLowerCase()}-${s}`} title={title}
          className="inline-flex w-max items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold" style={style}>
      {label}: {s}{s === "failed" && error ? " ⚠" : ""}
    </span>
  );
}

export default function Admin() {
  useNoIndex();
  const [key, setKey] = useState(localStorage.getItem("kapatia_admin_key") || "");
  const [verified, setVerified] = useState(false);
  const [input, setInput] = useState("");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, today: 0 });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [eq, setEq] = useState("");
  const [dateFilter, setDateFilter] = useState("all");

  const loadStats = useCallback(async (k) => {
    try {
      const r = await fetch(`${API}/enquiries/stats`, { headers: { "X-Admin-Key": k } });
      if (r.ok) setStats(await r.json());
    } catch (err) { console.debug("Failed to load enquiry stats", err); }
  }, []);

  const load = useCallback(async (k) => {
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/enquiries`, { headers: { "X-Admin-Key": k } });
      if (r.status === 401) {
        setError("Invalid admin key.");
        setRows([]);
        setVerified(false);
        localStorage.removeItem("kapatia_admin_key");
        setKey("");
        return false;
      }
      const data = await r.json();
      setRows(Array.isArray(data) ? data : []);
      setVerified(true);
      loadStats(k);
      return true;
    } catch (e) {
      setError("Could not reach server."); return false;
    } finally { setLoading(false); }
  }, [loadStats]);

  const markAllRead = async () => {
    await fetch(`${API}/enquiries/mark_all_read`, { method: "POST", headers: { "X-Admin-Key": key } });
    load(key);
  };

  const setStatus = async (r, status) => {
    setRows((list) => list.map((x) => x.id === r.id ? { ...x, status, read: true } : x));
    await fetch(`${API}/enquiries/${r.id}/status`, {
      method: "PUT", headers: { "Content-Type": "application/json", "X-Admin-Key": key },
      body: JSON.stringify({ status }),
    });
    loadStats(key);
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const cols = ["created_at", "name", "phone", "company", "city", "requirement", "status", "campaign", "source", "whatsapp_status", "email_status", "read"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Date", "Name", "Phone", "Company", "City", "Requirement", "Status", "Campaign", "Source", "WhatsApp", "Read"].join(",");
    const lines = rows.map((r) => cols.map((c) => esc(c === "created_at" ? new Date(r[c]).toLocaleString("en-IN") : r[c])).join(","));
    const csv = [header, ...lines].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kapatia-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => { if (key) load(key); }, [key, load]);

  const login = async (e) => {
    e.preventDefault();
    const ok = await load(input);
    if (ok) { localStorage.setItem("kapatia_admin_key", input); setKey(input); }
  };
  const logout = () => { localStorage.removeItem("kapatia_admin_key"); setKey(""); setVerified(false); setRows([]); setInput(""); };

  const filteredRows = (() => {
    const term = eq.trim().toLowerCase();
    const now = new Date();
    const days = dateFilter === "today" ? 1 : dateFilter === "7d" ? 7 : dateFilter === "30d" ? 30 : null;
    const since = days ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1)) : null;
    return rows.filter((r) => {
      const okTerm = !term || `${r.name} ${r.phone} ${r.company} ${r.city} ${r.requirement}`.toLowerCase().includes(term);
      const okDate = !since || new Date(r.created_at) >= since;
      return okTerm && okDate;
    });
  })();

  if (!verified) {
    return (
      <div className="grid min-h-screen place-items-center p-5" style={{ background: "var(--paper)" }}>
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl border bg-white p-7"
              style={{ borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
          <span className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: "var(--green-100)", color: "var(--green-800)" }}><Lock size={22} /></span>
          <h1 className="mt-4 font-heading text-[24px] font-extrabold" style={{ color: "var(--green-950)" }}>Kapatia Admin</h1>
          <p className="mt-1 text-[14px]" style={{ color: "var(--muted)" }}>
            {key && !error ? "Checking saved session\u2026" : "Enter your admin key to view enquiries."}
          </p>
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Admin key"
                 data-testid="admin-login-input"
                 className="mt-5 w-full rounded-lg border bg-white px-3.5 py-3 outline-none focus:ring-4" style={{ borderColor: "#d8c8af" }} />
          {error && <p data-testid="admin-login-error" className="mt-2 text-[13px] font-semibold" style={{ color: "var(--orange-700)" }}>{error}</p>}
          <button type="submit" data-testid="admin-login-submit" className="mt-4 w-full rounded-full py-3 font-heading font-bold text-white"
                  style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>Sign in</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--paper)" }}>
      <header className="border-b bg-white" style={{ borderColor: "var(--line)" }}>
        <div className="container flex flex-wrap items-center justify-between gap-3 py-4">
          <div className="no-scrollbar flex max-w-full items-center gap-2 overflow-x-auto rounded-full border p-1" style={{ borderColor: "var(--line)" }}>
            <button onClick={() => setTab("enquiries")}
                    className="relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-heading text-[13.5px] font-bold transition-colors"
                    style={tab === "enquiries" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
              <MessagesSquare size={15} /> Enquiries ({rows.length})
              {stats.unread > 0 && (
                <span className="grid h-5 min-w-[20px] place-items-center rounded-full px-1.5 text-[11px] font-extrabold text-white"
                      style={{ background: "linear-gradient(135deg, #ff3f1f, var(--orange-500))" }}>{stats.unread}</span>
              )}
            </button>
            <button onClick={() => setTab("inventory")}
                    className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-heading text-[13.5px] font-bold transition-colors"
                    style={tab === "inventory" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
              <Boxes size={15} /> Inventory
            </button>
            <button onClick={() => setTab("photos")} data-testid="admin-tab-photos"
                    className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-heading text-[13.5px] font-bold transition-colors"
                    style={tab === "photos" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
              <Camera size={15} /> Photos
            </button>
            <button onClick={() => setTab("insights")}
                    className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-heading text-[13.5px] font-bold transition-colors"
                    style={tab === "insights" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
              <BarChart3 size={15} /> Insights
            </button>
            <button onClick={() => setTab("sales")} data-testid="admin-tab-sales"
                    className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-heading text-[13.5px] font-bold transition-colors"
                    style={tab === "sales" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
              <Receipt size={15} /> Sales
            </button>
            <button onClick={() => setTab("settings")} data-testid="admin-tab-settings"
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-heading text-[13.5px] font-bold transition-colors"
                    style={tab === "settings" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
              <Settings size={15} /> Settings
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {tab === "enquiries" && (
              <>
                {stats.unread > 0 && (
                  <button onClick={markAllRead} className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-heading text-[13.5px] font-bold text-white"
                          style={{ background: "linear-gradient(135deg, var(--orange-700), var(--orange-500))" }}>Mark all read</button>
                )}
                <button onClick={exportCsv} disabled={rows.length === 0} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 font-heading text-[13.5px] font-bold disabled:opacity-50"
                        style={{ borderColor: "var(--line)", color: "var(--green-900)" }}><Download size={15} /> Export CSV</button>
                <button onClick={() => load(key)} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 font-heading text-[13.5px] font-bold"
                        style={{ borderColor: "var(--line)", color: "var(--green-900)" }}><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh</button>
              </>
            )}
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-heading text-[13.5px] font-bold text-white"
                    style={{ background: "var(--green-900)" }}><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>
      <main className="container py-8">
        {tab === "photos" ? (
          <AdminPhotos adminKey={key} />
        ) : tab === "inventory" ? (
          <AdminInventory adminKey={key} />
        ) : tab === "insights" ? (
          <AdminInsights adminKey={key} />
        ) : tab === "settings" ? (
          <AdminSettings adminKey={key} />
        ) : tab === "sales" ? (
          <AdminSales adminKey={key} />
        ) : (
        <>
          <div className="mb-6 grid grid-cols-3 gap-4" data-testid="admin-stats-cards">
            {[{ k: "unread", label: "Unread", hot: true }, { k: "today", label: "Today" }, { k: "total", label: "Total" }].map((s) => (
              <div key={s.k} className="rounded-2xl border bg-white px-5 py-5"
                   style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
                <div className="font-heading text-[30px] font-extrabold" style={{ color: s.hot && stats.unread > 0 ? "var(--orange-700)" : "var(--green-900)" }}>{stats[s.k]}</div>
                <div className="mt-0.5 text-[13px] font-semibold uppercase tracking-wide" style={{ color: "var(--muted)" }}>{s.label}</div>
              </div>
            ))}
          </div>
          {rows.length === 0 ? (
          <div className="grid place-items-center gap-3 rounded-2xl border bg-white py-20 text-center" style={{ borderColor: "var(--line)" }}>
            <Inbox size={40} style={{ color: "var(--muted)" }} />
            <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No enquiries yet</p>
          </div>
        ) : (
          <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-full border bg-white px-4 py-2" style={{ borderColor: "var(--line)" }}>
              <Search size={16} style={{ color: "var(--muted)" }} />
              <input value={eq} onChange={(e) => setEq(e.target.value)} placeholder="Search name, phone, company…"
                     className="w-60 bg-transparent text-[14px] outline-none" />
            </div>
            <div className="flex items-center gap-1.5 rounded-full border bg-white p-1" style={{ borderColor: "var(--line)" }}>
              {[{ k: "all", l: "All" }, { k: "today", l: "Today" }, { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }].map((d) => (
                <button key={d.k} onClick={() => setDateFilter(d.k)}
                        className="rounded-full px-3.5 py-1.5 font-heading text-[12.5px] font-bold transition-colors"
                        style={dateFilter === d.k ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
                  {d.l}
                </button>
              ))}
            </div>
            <span className="text-[13px] font-semibold" style={{ color: "var(--muted)" }}>{filteredRows.length} shown</span>
          </div>
          {filteredRows.length === 0 ? (
          <div className="grid place-items-center gap-3 rounded-2xl border bg-white py-16 text-center" style={{ borderColor: "var(--line)" }}>
            <Inbox size={36} style={{ color: "var(--muted)" }} />
            <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No enquiries match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border bg-white" data-testid="admin-enquiries-table" style={{ borderColor: "var(--line)" }}>
            <table className="w-full min-w-[820px] text-left text-[14px]">
              <thead>
                <tr style={{ background: "var(--green-100)", color: "var(--green-950)" }} className="font-heading text-[12.5px] uppercase tracking-wide">
                  <th className="px-4 py-3">Date</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Company</th><th className="px-4 py-3">City</th><th className="px-4 py-3">Requirement</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3">Notifications</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: "var(--line)", color: "#31463a", background: r.read ? "transparent" : "rgba(245,180,0,0.08)" }}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {!r.read && <span className="mr-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-extrabold text-white align-middle" style={{ background: "linear-gradient(135deg, #ff3f1f, var(--orange-500))" }}>NEW</span>}
                      {new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-4 py-3 font-semibold" style={{ color: "var(--green-950)" }}>
                      {r.name}
                      {r.campaign && (
                        <span className="ml-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold align-middle" style={{ background: "rgba(244,119,28,0.14)", color: "var(--orange-700)" }}>via {r.campaign}</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><a href={`tel:${r.phone}`} style={{ color: "var(--green-800)" }}>{r.phone}</a></td>
                    <td className="px-4 py-3">{r.company || "-"}</td>
                    <td className="px-4 py-3">{r.city || "-"}</td>
                    <td className="px-4 py-3 max-w-[280px]">{r.requirement || "-"}</td>
                    <td className="px-4 py-3">
                      <select value={r.status || "new"} onChange={(e) => setStatus(r, e.target.value)}
                              data-testid={`enquiry-status-${r.id}`}
                              className="rounded-full border px-2.5 py-1 font-heading text-[12px] font-bold outline-none"
                              style={{
                                borderColor: "var(--line)", cursor: "pointer",
                                background: { new: "rgba(245,180,0,0.15)", contacted: "rgba(45,106,79,0.12)", quoted: "rgba(244,119,28,0.14)", closed: "#eef1ee" }[r.status || "new"],
                                color: { new: "#8a6a00", contacted: "var(--green-800)", quoted: "var(--orange-700)", closed: "#5a6b62" }[r.status || "new"],
                              }}>
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="quoted">Quoted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1" data-testid={`enquiry-notify-${r.id}`}>
                        <NotifyBadge label="WA" status={r.whatsapp_status} error={r.whatsapp_error} at={r.whatsapp_notified_at} />
                        <NotifyBadge label="Email" status={r.email_status} error={r.email_error} at={r.email_notified_at} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          </>
          )}
        </>
        )}
      </main>
    </div>
  );
}
